"use client";

// Chrome-sphere shader blob positioned at the boundary between the
// Studio and Pourquoi sections (mounted inside Studio, translated 50%
// down so half spills into Pourquoi). Uses the LiquidMetal default
// preset with `shape="circle"` so the shader itself renders a sphere
// (no need for border-radius CSS clipping) and `colorBack` set to a
// transparent rgba so there's no gray fill around the sphere.
//
// Always-on by design. We deliberately don't gate on perf-level or
// `prefers-reduced-motion` — the studio liquid is part of the brand and
// must show up on every device. Owner override of the standard a11y
// default. The Hero loader's opaque z-[9999] backdrop hides the
// shader-compile burn so it's already running when the loader fades.

import { useEffect, useRef, useState } from "react";
import { LiquidMetal, liquidMetalPresets } from "@paper-design/shaders-react";

export function StudioLiquidBg() {
  const wrapRef = useRef<HTMLDivElement>(null);
  // SSR-safe: only mount the WebGL canvas after the component has
  // hydrated client-side. LiquidMetal calls `useEffect` internally and
  // is fine on the client, but we keep the `inView` gate anyway so the
  // GPU isn't running the shader while the user is elsewhere on the
  // page (the chrome sphere is the heaviest WebGL frame on the site).
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: "300px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      aria-hidden
      className="pointer-events-none absolute inset-x-0 bottom-0 z-0 flex translate-y-1/2 justify-center"
    >
      <div
        className="aspect-square"
        style={{
          width: "min(75vw, 720px)",
          opacity: 0.8,
        }}
      >
        {inView && (
          <LiquidMetal
            {...liquidMetalPresets[0]}
            shape="circle"
            // The package's color parser rejects the literal "transparent"
            // keyword and falls back to opaque black — use rgba(0,0,0,0)
            // explicitly to actually get a transparent canvas.
            colorBack="rgba(0,0,0,0)"
            style={{ width: "100%", height: "100%" }}
          />
        )}
      </div>
    </div>
  );
}
